/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { isPlatformServer } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import {
	type ActivatedRouteSnapshot,
	type CanActivate,
	Router,
	type RouterStateSnapshot,
	type UrlTree,
} from "@angular/router";
import type { Session } from "@supabase/supabase-js";
import { type Observable, catchError, map, throwError } from "rxjs";
import { CbRoutesUtils } from "../@classes/routes/cb-routes-utils";
import { AuthStateService } from "../@services/shared/auth-state.service";
import { AuthService } from "../@services/shared/auth.service";
import { ErrorHandlerService } from "../@services/shared/error-handler.service";
import { LoggerService } from "../@services/shared/logger.service";

@Injectable({
	providedIn: "root",
})
export class AuthGuard implements CanActivate {
	private readonly loginUrl = CbRoutesUtils.paths.auth.login.path;

	private readonly router: Router = inject(Router);
	public errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
	public loggerService: LoggerService = inject(LoggerService);

	private readonly platformId = inject(PLATFORM_ID);
	private readonly authService: AuthService = inject(AuthService);
	private readonly authStateService: AuthStateService =
		inject(AuthStateService);

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	):
		| Observable<boolean | UrlTree>
		| Promise<boolean | UrlTree>
		| boolean
		| UrlTree {
		const currentRoute = route.url.map((p) => p.path).join("/");

		this.loggerService.logInfo(
			`[AuthGuard] Checking if user is authenticated in route "${currentRoute}"`,
		);

		if (isPlatformServer(this.platformId)) {
			return true;
		}

		const checkSession = (session: Session | null) => {
			if (!session) {
				this.loggerService.logWarning("No session found, redirecting to login");
				this.router.navigate([CbRoutesUtils.paths.auth.login.path]);
				return false;
			}

			// If the user is not confirmed, redirect to the confirm account page
			if (!session.user.email_confirmed_at) {
				this.loggerService.logWarning(
					"User not confirmed, redirecting to confirm account page.",
				);
				this.router.navigate([CbRoutesUtils.paths.auth.confirmAccount.path]);
				return false;
			}

			return true;
		};

		if (this.authStateService.currentAuthSession()) {
			return checkSession(this.authStateService.currentAuthSession());
		}

		return this.authService.updateSession().pipe(
			map((session) => {
				return checkSession(session);
			}),
			catchError((error) =>
				throwError(() => this.errorHandlerService.handleError(error, false)),
			),
			catchError((error) => {
				this.loggerService.logError("No session found");
				return throwError(() => this.router.navigate([this.loginUrl]));
			}),
		);
	}
}
